import React, { useState, useEffect, useMemo } from "react";
import { Button, Input } from "reactstrap";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { getAuthUser } from "../../../helpers/utils";
import PaginationControls from "./PaginationControls";
import DataTable from "react-data-table-component";
import "../../../App.css";

const StatusObj = { // As per your request
  1: "Pending",
  2: "OnGoing",
  3: "Completed",
  4: "Cancelled"
};

const CommonTable = ({
  data,
  tableColumnNames = [],
  searchTerm,
  onEditClickHandler,
  onActiveOrDeactiveChange,
  setDeleteModal,
  setSelectedIds,
  handleCheckbox,
  handleNavigate,
  handleCancel,
  onInlineUpdate,
  handleButtonClick,
  hasAction = true,
  permission
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [sortedData, setSortedData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const { user } = getAuthUser();

  // State for inline editing
  const [editingCell, setEditingCell] = useState({ rowId: null, columnKey: null });
  const [editedValue, setEditedValue] = useState("");

  // Pagination
  const paginatedData = sortedData?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  
  
  // Filter columns based on user permissions (UserId or RoleId)
  const filteredColumns = useMemo(() => {
    let columns = []
    const hasColumns = tableColumnNames.some((col) => col?.UserId?.includes(user.UserId));

    if (hasColumns) {
      columns =  tableColumnNames.filter((col) => col?.UserId?.includes(user?.UserId));
    }else{
      columns =  tableColumnNames.filter((col) => col?.RoleId?.includes(user?.RoleId)); 
    }

    if (!permission?.CanActive) {
      columns = columns.filter((col) => col.FieldName !== "Active");
    }
    
    return columns;
  }, [tableColumnNames, user?.UserId, user?.RoleId, permission?.CanActive]);


  // handleDelete function
  const handleDelete = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
    setDeleteModal(true);
  };

  // handleDelete handleSort
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // handleRowSelect function
  const handleRowSelect = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  // handleSelectAll function
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = paginatedData?.map((item) => {
        return item.Id ? item.Id : item.ID; // Check for both Id and ID
      });
      setSelectedRows(allIds);
      setSelectedIds(allIds);
    } else {
      setSelectedRows([]);
      setSelectedIds([]);
    }
  };

  // UseEffects
  useEffect(() => {
    let filteredData = data?.filter((item) =>
      tableColumnNames.some((col) => {
        const value = item[col.Title];
    
        // Handle nested object like RoleName
        if (value && typeof value === "object") {
          return Object.values(value).some((nestedVal) =>
            nestedVal?.toString().toLowerCase().includes(searchTerm.toLowerCase())
          );
        }
    
        // Handle normal values
        return value?.toString().toLowerCase().includes(searchTerm.toLowerCase());
      })
    );
    
    

    if (sortConfig.key) {
      filteredData.sort((a, b) => {
        // const aValue = a[sortConfig.key] ?? "";
        // const bValue = b[sortConfig.key] ?? "";
        const aValue = a[sortConfig.key] ? a[sortConfig.key].toString() : "";
        const bValue = b[sortConfig.key] ? b[sortConfig.key].toString() : "";
        return sortConfig.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      });
    }

    setSortedData(filteredData);
  }, [searchTerm, data, sortConfig]);

  return (
    <React.Fragment>
      <div className="table-responsive table-card mb-1">
        {paginatedData?.length > 0 ? (
          <table className="table align-middle table-nowrap">
            <thead className="table-light">
              <tr>
                {hasAction === true && (
                  <th scope="col" style={{ width: "50px" }}>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={selectedRows.length === paginatedData?.length}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                )}
                {filteredColumns.map((col, index) => (                  
                  col.FieldName == "Active" ? <th>{i18n.language === "ar" ? col?.ValueAr : col?.ValueEn}{" "}</th> :
                  <th key={index} onClick={() => handleSort(col.Title)}>
                    {i18n.language === "ar" ? col?.ValueAr : col?.ValueEn}{" "}
                    {sortConfig.key === col.Title
                      ? sortConfig.direction === "asc"
                        ? "▲"
                        : "▼"
                      : <i className="bx bxs-sort-alt"></i>
                    }
                  </th>
                ))}
                {hasAction === true && <th>{t("Action")}</th>}
              </tr>
            </thead>
            <tbody>
              {paginatedData?.map((item, index) => (
                <tr key={index}>
                  {hasAction === true && (
                    <th scope="row">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={selectedRows.includes(item.Id || item.ID)}
                          onChange={() => {
                            const Id = item.Id ? item.Id : item.ID;
                            handleRowSelect(Id)
                          }}
                        />
                      </div>
                    </th>
                  )}

                  {filteredColumns.map((col, index) => {
                    const isEditing = editingCell.rowId === (item.Id || item.ID) && editingCell.columnKey === col.Title;
                    let content = "N/A";

                    // NOTE: For 'InLine' case to work, ensure the corresponding column definition
                    // fetched from the backend (e.g., for 'Score') has its FieldName set to "InLine".

                    // Rendering logic based on FieldName
                    switch (col.FieldName) {
                      case "Normal":
                      if (typeof item[col.Title] === "object" && item[col.Title] !== null) {
                        content = (
                          <div className="wrap-text">
                            {item[col.Title][i18n.language] ?? "-"}
                          </div>
                        );
                      } else { // Fallback for Normal or if Label ID not in StatusObj
                        content = (
                          <div className="wrap-text">
                            {item[col.Title] ?? "-"}
                          </div>
                        );
                      }
                      break;

                      case "Label": 
                        if (col.FieldName === "Label" && StatusObj[item[col.Title]] !== undefined) {
                          content =(<><div className="wrap-text">{StatusObj[item[col.Title]]}</div></>)
                        }
                      break;

                      case "Button":
                        content = (
                          <> {
                            handleButtonClick && <>
                              { item[col.Title] != 0 ?
                                  <button type="button" className="btn btn-soft-secondary btn-sm" onClick={() => handleButtonClick(item, col.Title)}>
                                  {item[col.Title]}
                                  </button>
                                  :
                                  <button type="button" className="btn btn-soft-secondary btn-sm" onClick={() => handleButtonClick(item, col.Title)}>
                                    {t("Add")}
                                  </button>
                                }
                            </> }
                          </>
                        );
                      break;

                      case "Button2":
                        content = (
                          handleButtonClick && 
                            <button type="button" className="btn btn-soft-secondary btn-sm" onClick={() => handleButtonClick(item, col.Title)}>
                              {item[col.Title]}
                            </button>
                        );
                      break;

                      case "ButtonTxt":
                        content = (
                          <> {
                            handleButtonClick && <>
                              { item[col.Title] != null ?
                                  <button type="button" className="btn btn-soft-secondary btn-sm" onClick={() => handleButtonClick(item, col.Title)}>
                                   {t("View")}
                                  </button>
                                  :
                                  <button type="button" className="btn btn-soft-secondary btn-sm" onClick={() => handleButtonClick(item, col.Title)}>
                                    {t("Add")}
                                  </button>
                                }
                            </> }
                          </>
                        );
                      break;

                      case "Active":
                        content = (
                          <div className="d-flex justify-content-center form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id={`flexSwitchCheckDefault-${index}`}
                              onChange={(e) =>
                                onActiveOrDeactiveChange(item, e)
                              }
                              checked={item[col.Title] === true}
                            />
                            <label
                              className="form-check-label"
                              htmlFor={`flexSwitchCheckDefault-${index}`}
                            ></label>
                          </div>
                        );
                        break;

                      case "Checkbox":
                        content = (
                          <div className="form-check form-switch" style={{ textAlign: "center", verticalAlign: "middle" }}>
                            <input
                              type="checkbox"
                              checked={item[col.Title]}
                              onChange={(e) =>
                                handleCheckbox(e, { ...item }, col.Title)
                              }
                            />
                          </div>
                        );
                        break;

                      case "Icon":
                        content = (
                          <div className="avatar-group d-flex justify-content-center">
                            <Link to="#" className="avatar-group-item" onClick={(e) => e.preventDefault()}> {/* Prevent navigation */}
                            <img src={item[col.Title]} alt="" className="rounded-circle avatar-sm"
                              // onError={(e) => {
                              //   e.target.onerror = null; 
                              //   e.target.src = noImage;
                              // }}
                            />
                            </Link>
                          </div>
                        );
                        break;

                      case "Icon": {
                        const altText = i18n.language === 'ar' ? (col.ValueAr || col.Title) : (col.ValueEn || col.Title);
                        content = (
                          <div className="d-flex justify-content-center align-items-center" style={{ height: '100%' }}>
                            <img
                              src={item[col.Title]}
                              alt={altText || "Icon"}
                              className="rounded-circle avatar-xs"
                              style={{
                                objectFit: 'cover',
                                // width: '24px', 
                                // height: '24px',
                              }}
                              onError={(e) => {
                                e.target.onerror = null; 
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        );
                        break;
                      }

                      case "List":
                          if (Array.isArray(item[col.Title])) {
                            content = (
                              <ul>
                                {item[col.Title].map((val, idx) => (
                                  <li key={idx}>
                                    {typeof val === "object" && val !== null
                                      ? val[i18n.language] ?? "-"
                                      : String(val)}
                                  </li>
                                ))}
                              </ul>
                            );
                          }
                          break;

                      case "Date":
                        content = item[col.Title]
                          ? new Date(item[col.Title]).toLocaleDateString()
                          : "-";
                        break;

                      case "Time":
                        content = item[col.Title] ? (
                          <div style={{ direction: 'ltr' }}>
                            {new Date(item[col.Title]).toLocaleString()}
                          </div>
                        ) : "-";
                        break;
                        

                      case "DateTime":
                        content = item[col.Title]
                          ? new Date(item[col.Title]).toLocaleString()
                          : "-";
                        break;

                      case "Boolean":
                        content = item[col.Title] !== null || item[col.Title] !== undefined
                          ? item[col.Title]?.toString()
                          : "-";
                        break;

                      case "InLine":
                        if (permission?.CanEdit) {
                          if (isEditing) {
                            const originalValue = item[col.Title];
                            const inputType = typeof originalValue === 'number' ? 'number' : 'text';
                            content = (
                              <Input
                                type={inputType}
                                bsSize="sm"
                                value={editedValue}
                                onChange={(e) => setEditedValue(e.target.value)}
                                onBlur={() => {
                                  let valueToUpdate = editedValue;
                                  const originalValueString = originalValue?.toString() ?? "";

                                  if (inputType === 'number') {
                                    const parsedValue = parseFloat(editedValue);
                                    if (!isNaN(parsedValue)) {
                                      valueToUpdate = parsedValue;
                                    } else {
                                      console.warn(`Could not parse '${editedValue}' as a number for column ${col.Title}.`);
                                    }
                                  }
                                  if (onInlineUpdate && editedValue !== originalValueString) { 
                                    onInlineUpdate(item.Id || item.ID, col.Title, valueToUpdate);
                                  }
                                  setEditingCell({ rowId: null, columnKey: null }); // Exit editing mode
                                }}
                                autoFocus
                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} // Optional: Save on Enter
                              />
                            );
                          } else {
                            content = (
                              <div className="wrap-text editable-cell" onClick={() => { setEditingCell({ rowId: (item.Id || item.ID), columnKey: col.Title }); setEditedValue(item[col.Title] ?? ""); }}>
                                {item[col.Title] ?? "-"} <i className="ri-pencil-fill edit-icon ms-1"></i> {/* Optional: Add edit icon */}
                              </div>
                            );
                          }
                        } else { // If no edit permission, just display the value
                          content = <div className="wrap-text">{item[col.Title] ?? "-"}</div>;
                        }
                        break;


                      default:
                        content = "-";
                      }

                    return <td key={index}>{content}</td>;
                  })}

                  {/* Actions  */}

                  {hasAction && (
                    <td>
                      <div className="hstack gap-3 flex-wrap justify-content-center">
                            {handleCancel && item.SessionStatusID !== 4 && (
                              <Link
                                className="link-danger fs-15 mt-1"
                                onClick={() => {
                                  const Id = item.Id ? item.Id : item.ID;
                                  
                                  handleCancel(Id)
                                }}
                              >
                                <i className="bx bxs-x-circle"></i>
                              </Link>
                            )}

                            {permission?.CanEdit && (
                              <Link
                                className="link-success fs-15"
                                onClick={() => onEditClickHandler(item, "isEdit")}
                              >
                                <i className="ri-edit-2-line"></i>
                              </Link>
                            )}
                            {permission?.CanDelete && (
                              <Link
                                className="link-danger fs-15"
                                onClick={() => {
                                  const Id = item.Id ? item.Id : item.ID;
                                  
                                  handleDelete(Id)
                                }}
                              >
                                <i className="ri-delete-bin-line"></i>
                              </Link>
                            )}
                        {handleNavigate && (
                          <Button className="action-link" onClick={() => handleNavigate(item.Id)}>
                            <i className="ri-shield-user-fill"></i>
                          </Button>
                        )}
                      </div>
                    </td>
                  )}

                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="noresult text-center">
            <h5 className="mt-2">{t("NoData")}</h5>
          </div>
        )}

        {/* Pagination Controls */}

        <PaginationControls
          totalEntries={data?.length}
          rowsPerPage={itemsPerPage}
          setRowsPerPage={setItemsPerPage}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </div>
    </React.Fragment>
  );
};

export const BasicTable = ({ title, data, columns }) => {
	const { t, i18n } = useTranslation();

	return (
		<DataTable
			title={title}
			columns={columns}
			data={data}
			responsive
			pagination={{
				enabled: true,
				defaultPage: 1,
				defaultRowsPerPage: 50,
				rowsPerPageOptions: [50, 100, 200],
				totalRows: data.length,
			}}
			paginationPerPage={50}
			paginationRowsPerPageOptions={[50, 100, 200]}
			highlightOnHover
			striped
			noDataComponent={
				<div style={{ padding: '24px', textAlign: 'center' }}>
					{t('NoData')}
				</div>
			}
			paginationComponentOptions={{
				rowsPerPageText: t('RowsPerPage'),  
				rangeSeparatorText: t('of'), 
				selectAllRowsItem: false,
				selectAllRowsItemText: t('All'), 
			}}
		/>
	);
};

export const NestedTable = ({ title, data, columns }) => {
	const { t, i18n } = useTranslation();

	return (
		<div style={{ padding: "20px 60px", border: "1px solid #ddd", borderRadius: "5px" }}>
			<DataTable
				title={title}
				columns={columns}
				data={data}
				striped
				responsive
				highlightOnHover
				pagination={false}
				noDataComponent={
					<div style={{ padding: '24px', textAlign: 'center' }}>
						{t('There are no records to display')}
					</div>
				}
			/>
		</div>
	);
};


export default CommonTable;
